import calculate from 'src/utils/calculate'

import dates from '@/utils/dates'
import format from '@/utils/format'

import { addDashboard, deleteDashboard, editDashboard } from './utils/dashboard'
import PATHS from '@/router/paths'

const actions = {
  async getData({ dispatch }, { params }) {
    const updateView = () => {
      const { baseReportName, routeName } = params
      const endpointKey = baseReportName || routeName
      return dispatch('tools/updateView', { endpointKey }, { root: true })
    }

    return updateView()
  },

  async getDashboardCard({ dispatch }, { metric, params, query }) {
    const { metricType, metricGroupBy } = metric
    const endpointKey = params.baseReportName || params.routeName
    const data = await dispatch('tools/request', { endpointKey, params, query }, { root: true })
    const items = await dispatch('tools/transform', { endpointKey, data, query }, { root: true })
    const groups = await dispatch('tools/applyReportCriteria', { items, params, query }, { root: true })

    return {
      metricType,
      metricGroupBy,
      [metricType.value]: calculate[metricType.value](groups),
      [metricGroupBy.value]: calculate[metricGroupBy.value](groups),
    }
  },

  async getPersonsCard({ dispatch }, { params, query }) {
    const endpointKey = 'client-assignments'
    const data = await dispatch('tools/request', { endpointKey, params, query }, { root: true })
    const items = await dispatch('tools/transform', { endpointKey, data, query }, { root: true })

    return {
      assignments: items,
      totalCount: items.length,
    }
  },
  async getTimeCard({ dispatch, rootState }, { params, query }) {
    const endpointKey = 'time-entries'
    const timeEntriesResponse = await dispatch('tools/request', { endpointKey, params, query }, { root: true })
    const { content: timeEntries } = timeEntriesResponse
    const entries = timeEntries.filter((entry) => entry.billingCode === query.billingCode)

    const { defaults: timeSummaryConfigs } = rootState.tools.summaries[PATHS.INTERNAL.TIME_SHEET]
    const summaryKeys = Object.keys(timeSummaryConfigs)
    const seed = summaryKeys.reduce((summaryDictionary, summaryKey) => {
      summaryDictionary[summaryKey] = { ...timeSummaryConfigs[summaryKey], items: [], total: 0 }
      return summaryDictionary
    }, {})

    if (!entries || !entries.length) return seed

    const timeSummaries = entries.reduce((original, entry) => {
      const {
        billableActionDuration: durationInMinutes,
        timeClassificationName: timeType,
      } = entry

      if (!original[timeType]) return original

      const { total: currentMinutes } = original[timeType]
      const total = currentMinutes + durationInMinutes

      const { timeEntryDate } = entry
      const monthsFromNow = dates.monthsFromNow(timeEntryDate)
      const notFromCurrentMonth = monthsFromNow !== 0

      if (notFromCurrentMonth) return original

      original[timeType].items.push(entry)
      original[timeType].total = total

      original.TOTAL.items.push(entry)
      original.TOTAL.total += durationInMinutes

      return original
    }, seed)

    return Object.keys(timeSummaries).reduce((formattedTimeSummaries, timeSummaryKey) => {
      const { total } = timeSummaries[timeSummaryKey]
      const totalFormatted = format.hhmm(total)

      formattedTimeSummaries[timeSummaryKey].totalFormatted = totalFormatted

      return formattedTimeSummaries
    }, timeSummaries)
  },

  errorSaving({ commit }, error) {
    const message = `Error Saving Dashboard: ${error.message}`
    const status = 'error'

    commit('tools/snackbar', true, { root: true })
    commit('tools/snack', { message, status }, { root: true })

    return error
  },
  submitFormDashboard({ commit, dispatch }, dashboard) {
    const saveEdits = async () => {
      const changes = { dashboard }
      const fnSuccess = () => {
        const message = 'Dashboard Saved'
        const status = 'success'

        commit('tools/snackbar', true, { root: true })
        commit('tools/snack', { message, status }, { root: true })
      }

      return dispatch('auth/handleUpdateToUserSettings', { changes, fnSuccess }, { root: true })
    }
    const catcher = (error) => dispatch('errorSaving', error)

    return saveEdits().catch(catcher)
  },

  addDashboard({ getters }, dashboardToAdd) {
    return addDashboard(dashboardToAdd, getters.dashboards)
  },
  editDashboard({ getters }, { dashboardEdits, indexToEdit }) {
    return editDashboard(indexToEdit, dashboardEdits, getters.dashboards)
  },
  removeDashboard({ getters }, indexToDelete) {
    return deleteDashboard(indexToDelete, getters.dashboards)
  },

  async prepareToCreateDashboard({ commit, dispatch }, { selectedReport }) {
    const dashboard = await dispatch('addDashboard', selectedReport)
    commit('formDashboard', { action: 'create', entry: dashboard, scope: 'item' })
  },

  upsertDashboard({ dispatch, getters, rootGetters }, { reportCode, dashboardsForOrg }) {
    const dashboardSettings = rootGetters['auth/dashboardSettings']
    const { haveDashboardSettings } = getters
    const updateDashboardSettings = () => {
      const dashboard = { ...dashboardSettings, [reportCode]: dashboardsForOrg }
      return dispatch('submitFormDashboard', dashboard)
    }
    const makeDashboardSettings = () => {
      const dashboard = {
        [reportCode]: dashboardsForOrg,
      }
      return dispatch('submitFormDashboard', dashboard)
    }

    return haveDashboardSettings ? updateDashboardSettings() : makeDashboardSettings()
  },

  async createDashboard({ dispatch, state }, reportCode) {
    const dashboardsForOrg = state.formDashboard.entry
    return dispatch('upsertDashboard', { reportCode, dashboardsForOrg })
  },
  async deleteDashboard({ dispatch }, { indexToDelete, reportCode }) {
    const dashboardsForOrg = await dispatch('removeDashboard', indexToDelete)
    return dispatch('upsertDashboard', { reportCode, dashboardsForOrg })
  },
  async updateDashboard({ dispatch }, { selectedItem, selectedReport }) {
    const reportCode = `${selectedReport.associationId}/${selectedReport.subId}`
    const dashboardsForOrg = await dispatch('editDashboard', {
      dashboardEdits: selectedReport,
      indexToEdit: selectedItem.index,
    })

    return dispatch('upsertDashboard', { reportCode, dashboardsForOrg })
  },
}

export default actions
